import { useState, useEffect, type FormEvent } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { auth as authApi } from '@/lib/api';
import { formatCurrency, formatDate } from '@/lib/utils';
import type { UserStats } from '@/lib/types';
import {
  User,
  Mail,
  Calendar,
  Shield,
  LogOut,
  Edit2,
  Check,
  X,
  Loader2,
  Lock,
  BarChart3,
  Trash2,
  Crown,
  AlertTriangle,
  TrendingUp,
  TrendingDown,
  Wallet,
  Target,
  FolderOpen,
  Activity,
  Sparkles,
  ChevronRight,
  Eye,
  EyeOff,
  Info,
} from 'lucide-react';

export default function SettingsPage() {
  const { user, logout, updateUser } = useAuth();
  const isPro = user?.plan === 'pro' || user?.plan === 'enterprise';

  // Profile editing
  const [editingName, setEditingName] = useState(false);
  const [nameValue, setNameValue] = useState(user?.name || '');
  const [nameLoading, setNameLoading] = useState(false);
  const [nameError, setNameError] = useState('');
  const [nameSuccess, setNameSuccess] = useState(false);

  // Password change
  const [showPasswordForm, setShowPasswordForm] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [passwordError, setPasswordError] = useState('');
  const [passwordSuccess, setPasswordSuccess] = useState(false);
  const [showCurrentPw, setShowCurrentPw] = useState(false);
  const [showNewPw, setShowNewPw] = useState(false);

  // Account stats
  const [stats, setStats] = useState<UserStats | null>(null);
  const [statsLoading, setStatsLoading] = useState(true);

  // Delete account
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [deleteLoading, setDeleteLoading] = useState(false);

  // Keyboard shortcuts info
  const [showShortcuts, setShowShortcuts] = useState(false);

  useEffect(() => {
    loadStats();
  }, []);

  async function loadStats() {
    try {
      const data = await authApi.getStats();
      setStats(data);
    } catch {
      // Stats failed to load, not critical
    } finally {
      setStatsLoading(false);
    }
  }

  async function handleNameSave(e: FormEvent) {
    e.preventDefault();
    if (!nameValue.trim()) return;
    setNameLoading(true);
    setNameError('');
    setNameSuccess(false);

    try {
      const updatedUser = await authApi.updateProfile(nameValue.trim());
      updateUser(updatedUser);
      setEditingName(false);
      setNameSuccess(true);
      setTimeout(() => setNameSuccess(false), 3000);
    } catch (err) {
      setNameError(err instanceof Error ? err.message : 'Failed to update name');
    } finally {
      setNameLoading(false);
    }
  }

  async function handlePasswordChange(e: FormEvent) {
    e.preventDefault();
    setPasswordError('');
    setPasswordSuccess(false);

    if (newPassword.length < 8) {
      setPasswordError('New password must be at least 8 characters');
      return;
    }

    if (newPassword !== confirmPassword) {
      setPasswordError('Passwords do not match');
      return;
    }

    setPasswordLoading(true);

    try {
      await authApi.changePassword(currentPassword, newPassword);
      setPasswordSuccess(true);
      setShowPasswordForm(false);
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      setTimeout(() => setPasswordSuccess(false), 3000);
    } catch (err) {
      setPasswordError(err instanceof Error ? err.message : 'Failed to change password');
    } finally {
      setPasswordLoading(false);
    }
  }

  async function handleDeleteAccount() {
    if (deleteConfirmText !== 'DELETE') return;
    setDeleteLoading(true);

    try {
      await authApi.deleteAccount();
      logout();
    } catch {
      setDeleteLoading(false);
    }
  }

  return (
    <div className="animate-fadeIn space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-white">Settings</h1>
        <p className="mt-1 text-gray-400">Manage your account and preferences</p>
      </div>

      {/* Success notifications */}
      {nameSuccess && (
        <div className="animate-slideUp rounded-lg bg-accent-green/10 px-4 py-3 text-sm text-accent-green flex items-center gap-2">
          <Check size={16} />
          Name updated successfully
        </div>
      )}
      {passwordSuccess && (
        <div className="animate-slideUp rounded-lg bg-accent-green/10 px-4 py-3 text-sm text-accent-green flex items-center gap-2">
          <Check size={16} />
          Password changed successfully
        </div>
      )}

      {/* Profile Section */}
      <div className="card">
        <h2 className="mb-4 text-lg font-semibold text-white flex items-center gap-2">
          <User size={20} className="text-brand-400" />
          Profile
        </h2>
        <div className="space-y-4">
          {/* Avatar + Name */}
          <div className="flex items-center gap-4 rounded-lg bg-surface-tertiary p-4">
            <div className="flex h-14 w-14 items-center justify-center rounded-full bg-gradient-to-br from-brand-500 to-brand-700 text-xl font-bold text-white shadow-lg shadow-brand-500/20">
              {user?.name?.charAt(0)?.toUpperCase() || 'U'}
            </div>
            <div className="flex-1">
              {editingName ? (
                <form onSubmit={handleNameSave} className="flex items-center gap-2">
                  <input
                    type="text"
                    value={nameValue}
                    onChange={(e) => setNameValue(e.target.value)}
                    className="input-base max-w-[200px]"
                    autoFocus
                    maxLength={100}
                  />
                  <button
                    type="submit"
                    disabled={nameLoading || !nameValue.trim()}
                    className="rounded-lg p-2 text-accent-green hover:bg-accent-green/10 disabled:opacity-50"
                  >
                    {nameLoading ? <Loader2 size={16} className="animate-spin" /> : <Check size={16} />}
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setEditingName(false);
                      setNameValue(user?.name || '');
                      setNameError('');
                    }}
                    className="rounded-lg p-2 text-gray-400 hover:bg-surface-hover hover:text-white"
                  >
                    <X size={16} />
                  </button>
                </form>
              ) : (
                <div className="flex items-center gap-2">
                  <p className="font-medium text-white">{user?.name || 'User'}</p>
                  <button
                    onClick={() => {
                      setEditingName(true);
                      setNameValue(user?.name || '');
                    }}
                    className="rounded-lg p-1 text-gray-500 hover:text-brand-400"
                  >
                    <Edit2 size={14} />
                  </button>
                </div>
              )}
              <p className="text-sm text-gray-400">{user?.email || ''}</p>
              {nameError && (
                <p className="mt-1 text-xs text-accent-red">{nameError}</p>
              )}
            </div>
            {/* Plan badge */}
            <div className={`flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-medium ${
              isPro
                ? 'bg-gradient-to-r from-amber-500/20 to-orange-500/20 text-amber-400'
                : 'bg-surface-secondary text-gray-400'
            }`}>
              {isPro && <Crown size={12} />}
              {user?.plan === 'enterprise' ? 'Enterprise' : isPro ? 'Pro' : 'Free'}
            </div>
          </div>

          {/* Account details grid */}
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
            <div className="rounded-lg bg-surface-tertiary p-4">
              <div className="flex items-center gap-2 text-gray-400">
                <Mail size={16} />
                <span className="text-sm">Email</span>
              </div>
              <p className="mt-1 text-sm text-white truncate">
                {user?.email || 'Not set'}
              </p>
            </div>

            <div className="rounded-lg bg-surface-tertiary p-4">
              <div className="flex items-center gap-2 text-gray-400">
                <Shield size={16} />
                <span className="text-sm">Plan</span>
              </div>
              <p className="mt-1 text-sm capitalize text-white">
                {user?.plan || 'Free'}
              </p>
            </div>

            <div className="rounded-lg bg-surface-tertiary p-4">
              <div className="flex items-center gap-2 text-gray-400">
                <Calendar size={16} />
                <span className="text-sm">Member Since</span>
              </div>
              <p className="mt-1 text-sm text-white">
                {user?.createdAt ? formatDate(user.createdAt) : 'Unknown'}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Account Stats */}
      <div className="card">
        <h2 className="mb-4 text-lg font-semibold text-white flex items-center gap-2">
          <BarChart3 size={20} className="text-brand-400" />
          Account Stats
        </h2>
        {statsLoading ? (
          <div className="flex justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin text-brand-500" />
          </div>
        ) : stats ? (
          <div className="space-y-4">
            {/* Financial summary */}
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
              <div className="rounded-lg bg-surface-tertiary p-4">
                <div className="flex items-center gap-2 text-accent-green">
                  <TrendingUp size={16} />
                  <span className="text-sm">All-Time Income</span>
                </div>
                <p className="mt-1 text-lg font-bold text-accent-green">
                  {formatCurrency(stats.totalIncome)}
                </p>
              </div>
              <div className="rounded-lg bg-surface-tertiary p-4">
                <div className="flex items-center gap-2 text-accent-red">
                  <TrendingDown size={16} />
                  <span className="text-sm">All-Time Expenses</span>
                </div>
                <p className="mt-1 text-lg font-bold text-accent-red">
                  {formatCurrency(stats.totalExpenses)}
                </p>
              </div>
              <div className="rounded-lg bg-surface-tertiary p-4">
                <div className="flex items-center gap-2 text-brand-400">
                  <Wallet size={16} />
                  <span className="text-sm">Net Worth</span>
                </div>
                <p className={`mt-1 text-lg font-bold ${stats.netWorth >= 0 ? 'text-accent-green' : 'text-accent-red'}`}>
                  {formatCurrency(stats.netWorth)}
                </p>
              </div>
            </div>

            {/* Activity stats */}
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
              <div className="rounded-lg bg-surface-tertiary p-3 text-center">
                <div className="flex items-center justify-center gap-1 text-gray-400">
                  <Activity size={14} />
                </div>
                <p className="mt-1 text-xl font-bold text-white">{stats.totalTransactions}</p>
                <p className="text-xs text-gray-500">Transactions</p>
              </div>
              <div className="rounded-lg bg-surface-tertiary p-3 text-center">
                <div className="flex items-center justify-center gap-1 text-gray-400">
                  <FolderOpen size={14} />
                </div>
                <p className="mt-1 text-xl font-bold text-white">{stats.customCategories}</p>
                <p className="text-xs text-gray-500">Custom Categories</p>
              </div>
              <div className="rounded-lg bg-surface-tertiary p-3 text-center">
                <div className="flex items-center justify-center gap-1 text-gray-400">
                  <Target size={14} />
                </div>
                <p className="mt-1 text-xl font-bold text-white">{stats.activeBudgets}</p>
                <p className="text-xs text-gray-500">Active Budgets</p>
              </div>
              <div className="rounded-lg bg-surface-tertiary p-3 text-center">
                <div className="flex items-center justify-center gap-1 text-gray-400">
                  <Sparkles size={14} />
                </div>
                <p className="mt-1 text-xl font-bold text-white">{stats.activeDays}</p>
                <p className="text-xs text-gray-500">Active Days</p>
              </div>
            </div>

            {stats.trackingSince && (
              <p className="text-center text-xs text-gray-500">
                Tracking finances since {formatDate(stats.trackingSince)}
              </p>
            )}
          </div>
        ) : (
          <p className="py-8 text-center text-sm text-gray-500">
            Start adding transactions to see your stats
          </p>
        )}
      </div>

      {/* Security Section */}
      <div className="card">
        <h2 className="mb-4 text-lg font-semibold text-white flex items-center gap-2">
          <Lock size={20} className="text-brand-400" />
          Security
        </h2>
        <div className="space-y-4">
          {/* Change Password */}
          <div className="rounded-lg bg-surface-tertiary p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-white">Password</p>
                <p className="text-xs text-gray-500">
                  Change your account password
                </p>
              </div>
              {!showPasswordForm && (
                <button
                  onClick={() => setShowPasswordForm(true)}
                  className="btn-secondary text-xs"
                >
                  <Lock size={14} className="mr-1.5" />
                  Change Password
                </button>
              )}
            </div>

            {showPasswordForm && (
              <form onSubmit={handlePasswordChange} className="mt-4 space-y-3">
                {passwordError && (
                  <div className="rounded-lg bg-accent-red/10 px-3 py-2 text-xs text-accent-red">
                    {passwordError}
                  </div>
                )}

                <div className="relative">
                  <input
                    type={showCurrentPw ? 'text' : 'password'}
                    value={currentPassword}
                    onChange={(e) => setCurrentPassword(e.target.value)}
                    placeholder="Current password"
                    required
                    className="input-base pr-10"
                  />
                  <button
                    type="button"
                    onClick={() => setShowCurrentPw(!showCurrentPw)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-white"
                  >
                    {showCurrentPw ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>

                <div className="relative">
                  <input
                    type={showNewPw ? 'text' : 'password'}
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    placeholder="New password (min 8 characters)"
                    required
                    minLength={8}
                    className="input-base pr-10"
                  />
                  <button
                    type="button"
                    onClick={() => setShowNewPw(!showNewPw)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-white"
                  >
                    {showNewPw ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>

                <input
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Confirm new password"
                  required
                  className="input-base"
                />

                {/* Password strength indicator */}
                {newPassword && (
                  <div className="space-y-1">
                    <div className="flex gap-1">
                      {[1, 2, 3, 4].map((level) => {
                        const strength = getPasswordStrength(newPassword);
                        return (
                          <div
                            key={level}
                            className={`h-1 flex-1 rounded-full ${
                              level <= strength
                                ? strength <= 1
                                  ? 'bg-accent-red'
                                  : strength <= 2
                                    ? 'bg-accent-yellow'
                                    : 'bg-accent-green'
                                : 'bg-surface-secondary'
                            }`}
                          />
                        );
                      })}
                    </div>
                    <p className="text-xs text-gray-500">
                      {getPasswordStrengthLabel(newPassword)}
                    </p>
                  </div>
                )}

                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => {
                      setShowPasswordForm(false);
                      setCurrentPassword('');
                      setNewPassword('');
                      setConfirmPassword('');
                      setPasswordError('');
                    }}
                    className="btn-secondary flex-1 text-xs"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={passwordLoading}
                    className="btn-primary flex-1 text-xs"
                  >
                    {passwordLoading ? (
                      <Loader2 size={14} className="animate-spin" />
                    ) : (
                      'Update Password'
                    )}
                  </button>
                </div>
              </form>
            )}
          </div>

          {/* Session info */}
          <div className="rounded-lg bg-surface-tertiary p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-white">Active Session</p>
                <p className="text-xs text-gray-500">
                  You are currently signed in
                </p>
              </div>
              <div className="flex items-center gap-2">
                <div className="h-2 w-2 rounded-full bg-accent-green animate-pulse" />
                <span className="text-xs text-accent-green">Active</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Plan & Features */}
      <div className="card">
        <h2 className="mb-4 text-lg font-semibold text-white flex items-center gap-2">
          <Crown size={20} className="text-amber-400" />
          Plan & Features
        </h2>
        <div className="space-y-3">
          <div className={`rounded-lg p-4 ${
            isPro
              ? 'bg-gradient-to-r from-amber-500/5 to-orange-500/5 border border-amber-500/20'
              : 'bg-surface-tertiary'
          }`}>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-white">
                  {isPro ? 'Pro Plan' : 'Free Plan'}
                </p>
                <p className="text-xs text-gray-500">
                  {isPro
                    ? 'You have access to all features'
                    : 'Upgrade to unlock all features'}
                </p>
              </div>
              {isPro ? (
                <span className="flex items-center gap-1 rounded-full bg-amber-500/20 px-3 py-1 text-xs font-medium text-amber-400">
                  <Crown size={12} />
                  Active
                </span>
              ) : (
                <button className="btn-primary text-xs bg-gradient-to-r from-amber-600 to-orange-600 hover:from-amber-500 hover:to-orange-500">
                  <Crown size={14} className="mr-1.5" />
                  Upgrade to Pro
                </button>
              )}
            </div>
          </div>

          {/* Feature comparison */}
          <div className="space-y-2">
            {[
              { feature: 'Transaction tracking', free: true, pro: true },
              { feature: 'Budget management', free: true, pro: true },
              { feature: 'Custom categories', free: '5 max', pro: 'Unlimited' },
              { feature: 'Transaction history', free: '3 months', pro: 'Unlimited' },
              { feature: 'CSV export', free: false, pro: true },
              { feature: 'Recurring transactions', free: false, pro: true },
            ].map(({ feature, free, pro }) => (
              <div
                key={feature}
                className="flex items-center justify-between rounded-lg bg-surface-tertiary px-4 py-2.5"
              >
                <span className="text-sm text-gray-300">{feature}</span>
                <div className="flex items-center gap-6">
                  <span className="w-20 text-center text-xs">
                    {free === true ? (
                      <Check size={14} className="mx-auto text-accent-green" />
                    ) : free === false ? (
                      <X size={14} className="mx-auto text-gray-600" />
                    ) : (
                      <span className="text-gray-400">{free}</span>
                    )}
                  </span>
                  <span className="w-20 text-center text-xs">
                    {pro === true ? (
                      <Check size={14} className="mx-auto text-accent-green" />
                    ) : (
                      <span className="text-amber-400">{pro}</span>
                    )}
                  </span>
                </div>
              </div>
            ))}
            <div className="flex items-center justify-end gap-6 px-4 pt-1">
              <span className="w-20 text-center text-xs text-gray-500">Free</span>
              <span className="w-20 text-center text-xs text-amber-400">Pro</span>
            </div>
          </div>
        </div>
      </div>

      {/* Keyboard Shortcuts */}
      <div className="card">
        <button
          onClick={() => setShowShortcuts(!showShortcuts)}
          className="flex w-full items-center justify-between"
        >
          <h2 className="text-lg font-semibold text-white flex items-center gap-2">
            <Info size={20} className="text-brand-400" />
            Quick Tips
          </h2>
          <ChevronRight
            size={20}
            className={`text-gray-400 transition-transform ${showShortcuts ? 'rotate-90' : ''}`}
          />
        </button>
        {showShortcuts && (
          <div className="mt-4 space-y-3 animate-slideUp">
            {[
              { tip: 'Use the search bar on the Transactions page to quickly find any transaction', icon: '/' },
              { tip: 'Set budgets for categories you frequently overspend on', icon: '$' },
              { tip: 'Create custom categories to organize your spending better', icon: '+' },
              { tip: 'Check the Dashboard daily to stay on top of your finances', icon: '#' },
              { tip: 'Pro users can export all transactions to CSV for tax season', icon: 'P' },
            ].map(({ tip, icon }) => (
              <div key={icon} className="flex items-start gap-3 rounded-lg bg-surface-tertiary p-3">
                <div className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-md bg-brand-500/10 text-xs font-mono font-bold text-brand-400">
                  {icon}
                </div>
                <p className="text-sm text-gray-400">{tip}</p>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Danger Zone */}
      <div className="card border-accent-red/20">
        <h2 className="mb-4 text-lg font-semibold text-accent-red flex items-center gap-2">
          <AlertTriangle size={20} />
          Danger Zone
        </h2>
        <div className="space-y-3">
          {/* Sign Out */}
          <div className="flex items-center justify-between rounded-lg bg-surface-tertiary p-4">
            <div>
              <p className="text-sm font-medium text-white">Sign Out</p>
              <p className="text-xs text-gray-500">
                Sign out of your account on this device
              </p>
            </div>
            <button onClick={logout} className="btn-secondary text-xs">
              <LogOut size={14} className="mr-1.5" />
              Sign Out
            </button>
          </div>

          {/* Delete Account */}
          <div className="rounded-lg bg-accent-red/5 p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-white">Delete Account</p>
                <p className="text-xs text-gray-500">
                  Permanently delete your account and all data
                </p>
              </div>
              {!showDeleteConfirm && (
                <button
                  onClick={() => setShowDeleteConfirm(true)}
                  className="btn-danger text-xs"
                >
                  <Trash2 size={14} className="mr-1.5" />
                  Delete Account
                </button>
              )}
            </div>

            {showDeleteConfirm && (
              <div className="mt-4 space-y-3 animate-slideUp">
                <div className="rounded-lg bg-accent-red/10 p-3">
                  <p className="text-xs text-accent-red">
                    <strong>Warning:</strong> This action is irreversible. All your transactions,
                    budgets, categories, and account data will be permanently deleted.
                  </p>
                </div>
                <p className="text-xs text-gray-400">
                  Type <strong className="text-white">DELETE</strong> to confirm:
                </p>
                <input
                  type="text"
                  value={deleteConfirmText}
                  onChange={(e) => setDeleteConfirmText(e.target.value)}
                  placeholder="Type DELETE to confirm"
                  className="input-base"
                />
                <div className="flex gap-2">
                  <button
                    onClick={() => {
                      setShowDeleteConfirm(false);
                      setDeleteConfirmText('');
                    }}
                    className="btn-secondary flex-1 text-xs"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleDeleteAccount}
                    disabled={deleteConfirmText !== 'DELETE' || deleteLoading}
                    className="btn-danger flex-1 text-xs"
                  >
                    {deleteLoading ? (
                      <Loader2 size={14} className="animate-spin" />
                    ) : (
                      'Permanently Delete Account'
                    )}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* App info */}
      <div className="text-center text-xs text-gray-600">
        <p>BudgetBoard v1.0.0</p>
        <p className="mt-1">Made with care for smarter finances</p>
      </div>
    </div>
  );
}

// Password strength helpers
function getPasswordStrength(password: string): number {
  let strength = 0;
  if (password.length >= 8) strength++;
  if (/[A-Z]/.test(password) && /[a-z]/.test(password)) strength++;
  if (/\d/.test(password)) strength++;
  if (/[^A-Za-z0-9]/.test(password)) strength++;
  return strength;
}

function getPasswordStrengthLabel(password: string): string {
  const strength = getPasswordStrength(password);
  if (strength <= 1) return 'Weak';
  if (strength === 2) return 'Fair';
  if (strength === 3) return 'Good';
  return 'Strong';
}
